import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/layout/PageHeader';
import { useCurrentUser } from '../auth';
import { ApiError } from '../../lib/api';
import type { ReportingBackfill } from '../../types/reporting';
import { listBackfills, retryBackfill } from './api';
import {
  anyBackfillRunning,
  backfillStatusChip,
  sortBackfillsNewestFirst,
} from './backfillView';

// Build B — the Backfills page (/reporting/backfills, admin-only). Every
// triggered Meta-history backfill with its status; polls every 10s while any row
// is running so status advances live (broadcast/preview dashboard pattern), and
// a Retry on failed rows re-fires as a new record.

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export function BackfillsPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const backfills = useQuery({
    queryKey: ['reporting', 'backfills'],
    queryFn: listBackfills,
    enabled: me.role === 'admin',
    // Poll while any backfill is running so status advances without a refresh.
    refetchInterval: (q) => (anyBackfillRunning(q.state.data?.backfills ?? []) ? 10000 : false),
  });

  const retry = useMutation({
    mutationFn: (b: ReportingBackfill) => retryBackfill(b.id),
    onMutate: (b) => {
      setRetryingId(b.id);
      setError(null);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reporting', 'backfills'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Retry failed.'),
    onSettled: () => setRetryingId(null),
  });

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can view reporting backfills.</p>;
  }

  const rows = sortBackfillsNewestFirst(backfills.data?.backfills ?? []);

  return (
    <div>
      <PageHeader
        title="Reporting backfills"
        description="On-demand full-history Meta pulls. Trigger one from an account's page; this list refreshes live while a backfill runs. Retry re-fires a failed backfill as a new run."
        backHref="/reporting"
      />

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {backfills.isLoading ? (
        <CenteredSpinner />
      ) : backfills.error ? (
        <p className="text-sm text-red-600">Couldn't load backfills.</p>
      ) : rows.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Account</th>
                <th className="px-4 py-2 font-medium">Triggered by</th>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Finished</th>
                <th className="px-4 py-2 font-medium">Error</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((b) => {
                const chip = backfillStatusChip(b.status);
                return (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-900">{b.accountName}</td>
                    <td className="px-4 py-2 text-slate-600">{b.triggeredByEmail}</td>
                    <td className="px-4 py-2 text-slate-600">{fmtDate(b.startedAt)}</td>
                    <td className="px-4 py-2">
                      <Badge variant={chip.variant}>{chip.label}</Badge>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{fmtDate(b.finishedAt)}</td>
                    <td className="px-4 py-2 text-slate-500 max-w-xs truncate" title={b.error ?? undefined}>
                      {b.error ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {b.status === 'failed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => retry.mutate(b)}
                          disabled={retryingId === b.id}
                        >
                          {retryingId === b.id ? 'Retrying…' : 'Retry'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          No backfills yet. Trigger one from an account's page (Reporting section).
        </p>
      )}
    </div>
  );
}
